import { Drawer } from "../../../components/Drawer";
import * as yup from "yup";
import { ButtonAction } from "../../../components/NewButtons";
import { FormProvider, useForm } from "react-hook-form";
import {
  Product,
  ProductClassification,
  UpdateProductPayload,
} from "../models/product";
import {
  transformPriceInput,
  validateAboveZero,
} from "../../../components/forms/inputs/PriceInput";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useUpdateProduct,
  useUploadProductImage,
} from "../service/products-api";
import * as authSelectors from "../../auth/authSelectors";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { css } from "@emotion/react";
import { ProductInformationForm } from "./ProductInformationForm";
import { ProductPricingForm } from "./ProductPricingForm";
import { transformPriceFromAPI } from "../../../util";
import { ProductImageForm } from "./ProductImageForm";
import { AdvancedSettingsForm } from "./AdvancedSettingsForm";
import { ProductAdditionGroupsForm } from "./ProductAdditionGroupsForm";
import { UpdatePackingItemForm } from "./UpdatePackingItemForm";
import { getErrorMessage } from "../../../error-handler";
import { ErrorMessage } from "@hookform/error-message";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { useEffect } from "react";

export type UpdateProductDrawerType =
  | "pricing"
  | "basic_information"
  | "image"
  | "advanced_options"
  | "packing_item_edit"
  | "addition_groups";

type Props = {
  isOpen: boolean;
  handleOnClose: () => void;
  product: Product;
  type: UpdateProductDrawerType;
  packingItemIndex?: number;
  drawerTitle: string;
};

const additionalFormStyles = css`
  gap: 16px;
  align-items: flex-start;
`;

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .max(255, "Admin name must be less than 255 characters")
    .required("Enter admin name to continue"),
  title: yup
    .string()
    .max(255, "Product name must be less than 255 characters")
    .required("Enter product name to continue"),
  priceAmount: validateAboveZero("priceCurrency").required(
    "Enter price to continue"
  ),
  reference: yup
    .string()
    .test("reference", "Must be less than 100 characters", (value) => {
      if (!value) {
        return true;
      }
      return value.length < 100;
    }),
  deliverectProductPlu: yup
    .string()
    .test(
      "deliverectProductPlu",
      "Must be less than 100 characters",
      (value) => {
        if (!value) {
          return true;
        }
        return value.length < 100;
      }
    ),
  onlinePosProductId: yup
    .string()
    .test("onlinePosProductId", "Must be less than 100 characters", (value) => {
      if (!value) {
        return true;
      }
      return value.length < 100;
    }),
  priceCurrency: yup.string().required("Choose currency to continue"),
  classification: yup
    .string()
    .required(
      "Product has no classification. This is a misconfiguration. Contact HeapsGo to fix."
    ),
  productCategoryId: yup
    .string()
    .test(
      "testing product category required",
      "Choose product category to continue",
      (val, ctx) => {
        if (ctx.parent.classification === ProductClassification.Addition) {
          return true;
        }
        return !!val;
      }
    ),
  description: yup
    .string()
    .test("description", "Must be less than 500 characters", (value) => {
      if (!value) {
        return true;
      }
      return value.length < 500;
    }),
  sortKey: yup
    .number()
    .typeError("Sort key must be greater than 0")
    .min(0, "Sort key must be greater than 0")
    .required("Enter sort key to continue"),
  isPriceDifferentiationEnabled: yup.boolean().oneOf([true, false]),
  countryCode: yup
    .string()
    .required(
      "Product has no country. This is a misconfiguration. Contact HeapsGo to fix."
    ),
  vat: yup
    .number()
    .typeError("VAT must be greater than 0")
    .min(0, "VAT must be greater than 0"),
  kitchen_capacity_units: yup
    .number()
    .min(0, "Kitchen capacity units must be greater than 0"),
  height: yup
    .number()
    .typeError("Height must be greater than 0")
    .min(0, "Height must be greater than 0"),
  length: yup
    .number()
    .typeError("Length must be greater than 0")
    .min(0, "Length must be greater than 0"),
  width: yup
    .number()
    .typeError("Width must be greater than 0")
    .min(0, "Width must be greater than 0"),
  weight: yup
    .number()
    .typeError("Weight must be greater than 0")
    .min(0, "Weight must be greater than 0"),
  alcoholPercentage: yup
    .number()
    .typeError("Alcohol percentage must be greater than 0")
    .min(0, "Alcohol percentage must be greater than 0")
    .max(100, "Alcohol percentage must be less than 100"),
  priceDeliveryAmount: yup.string().when("isPriceDifferentiationEnabled", {
    is: true,
    then: validateAboveZero("priceDeliveryCurrency").required(
      "Enter delivery price to continue"
    ),
    otherwise: yup.string().nullable(),
  }),
});

export type ProductUpdateFormValues = Omit<Product, "tags" | "labels"> & {
  tags: string[];
  files: File[];
  labels: string[];
};

export const UpdateProductDrawer = ({
  isOpen,
  handleOnClose,
  product,
  type,
  packingItemIndex,
  drawerTitle,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const formMethods = useForm<ProductUpdateFormValues>({
    resolver: yupResolver(validationSchema),
  });

  const {
    mutateAsync: updateProduct,
    isLoading: isUpdatingProduct,
  } = useUpdateProduct(product.id);
  const {
    mutateAsync: uploadProductImage,
    isLoading: isUpladingImage,
  } = useUploadProductImage(product.id);

  const onSubmit = async (product: ProductUpdateFormValues) => {
    const {
      files,
      priceAmount,
      priceCurrency,
      dimensions,
      packagingInstructions,
      additionGroups,
      ...rest
    } = product;

    const payload: UpdateProductPayload = {
      ...rest,
      productPackagingInstructions: [],
      priceCurrency,
      priceAmount: transformPriceInput(priceAmount, priceCurrency),
      additionGroups: [],
    };

    if (product.classification !== ProductClassification.VoucherOffer) {
      payload.voucherTypeId = "";
    }

    if (!payload.kitchenCategoryId) {
      payload.kitchenCategoryId = null;
    }

    if (!payload.productCategoryId) {
      payload.productCategoryId = null;
    }

    if (product.classification !== ProductClassification.VoucherStack) {
      payload.productPackId = "";
    }

    if (!payload.productCoolDownRulesetId) {
      payload.productCoolDownRulesetId = null;
    }

    if (!payload.servingGroupId) {
      payload.servingGroupId = "";
    }

    if (!payload.simphonyReference) {
      payload.simphonyReference = "";
    }

    if (payload.isPriceDifferentiationEnabled) {
      payload.priceDeliveryAmount = transformPriceInput(
        payload.priceDeliveryAmount,
        payload.priceCurrency
      );
      payload.priceDeliveryCurrency = payload.priceCurrency;
    } else {
      payload.priceDeliveryAmount = "";
      payload.priceDeliveryCurrency = "";
    }

    if (packagingInstructions && packagingInstructions.length > 0) {
      payload.productPackagingInstructions = packagingInstructions.map(
        (instruction) => ({
          amount: instruction.amount,
          id: instruction.id,
        })
      );
    }

    if (product.classification === ProductClassification.Generic) {
      payload.additionGroups = additionGroups?.map((x: any) => x.id) || [];
    }

    if (dimensions) {
      payload.height = dimensions.height;
      payload.length = dimensions.length;
      payload.width = dimensions.width;
    }

    try {
      await updateProduct({
        data: {
          ...payload,
        },
        organizationKeyName,
      });
      if (files?.length > 0) {
        await uploadProductImage({
          organizationKeyName,
          id: product.id,
          file: files[0],
        });
      }
      toastr.success("Success", `Product has been updated successfully`);
      handleOnClose();
      formMethods.reset();
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useEffect(() => {
    const { labels, ...rest } = product;
    formMethods.reset({
      ...rest,
      labels: labels.map((label) => label.id),
      priceAmount: transformPriceFromAPI(
        product.priceAmount,
        product.priceCurrency
      ),
      servingGroupId: product.servingGroupId || "",
      simphonyReference: product.simphonyReference || "",
      tags: product.tags.map((tag) => tag.id),
      priceDeliveryAmount: transformPriceFromAPI(
        product.priceDeliveryAmount as number,
        product.priceDeliveryCurrency
      ),
      isPriceDifferentiationEnabled:
        product.priceDeliveryAmount !== product.priceAmount,
      files: [],
    });
  }, [product, formMethods]);

  return (
    <Drawer
      title={drawerTitle}
      width={700}
      buttons={
        <ButtonAction
          onClick={formMethods.handleSubmit(onSubmit)}
          type="button"
          disabled={isUpdatingProduct || isUpladingImage}
        >
          {isUpdatingProduct || isUpladingImage ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={() => {
        formMethods.reset();
        handleOnClose();
      }}
    >
      <FormProvider {...formMethods}>
        <StyledForm additionalStyles={additionalFormStyles}>
          <ErrorMessage
            name="countryCode"
            render={({ message }) => (
              <StyledText
                className="bodyTextRegular mb-0"
                color={colors.errorDark}
              >
                {message}
              </StyledText>
            )}
          />
          <ErrorMessage
            name="classification"
            render={({ message }) => (
              <StyledText
                className="bodyTextRegular mb-0"
                color={colors.errorDark}
              >
                {message}
              </StyledText>
            )}
          />
          {type === "basic_information" && (
            <ProductInformationForm isEdit={true} />
          )}
          {type === "pricing" && (
            <ProductPricingForm isEdit={true} product={product} />
          )}
          {type === "image" && <ProductImageForm />}
          {type === "advanced_options" && (
            <AdvancedSettingsForm product={product} />
          )}
          {type === "addition_groups" && <ProductAdditionGroupsForm />}
          {type === "packing_item_edit" &&
            (packingItemIndex || packingItemIndex === 0) && (
              <UpdatePackingItemForm index={packingItemIndex} />
            )}
        </StyledForm>
      </FormProvider>
    </Drawer>
  );
};
